/**
 * Which auth failures the app reacts to, and where each one sends the user.
 *
 * Every failed request in the SPA passes through the response interceptor in
 * `api/client.ts`. Two of them are not really "this request failed" but "this
 * session is over": a 401 from a protected endpoint (expired/invalid session)
 * and a 403 carrying `password_change_required` (the backend has confined the
 * user to the change-password screen). The interceptor used to decide both
 * inline; the decision lives here so it can be read and tested without an
 * axios instance, a router or a Pinia store in the room.
 *
 * WHY THIS IS TWO FUNCTIONS AND NOT ONE. Do not merge them.
 * `classifyAuthFailure` runs on EVERY rejected request — a 404 from a scene
 * lookup, a 500 from anywhere. `resolveAuthRedirect` needs the current route,
 * which means the caller has to have loaded `@/router` first, and that import
 * is deliberately lazy. Folding the route check into the classifier would make
 * the interceptor instantiate the router module on every failure in the app —
 * including the 404s a scene lookup swallows on purpose — instead of only when
 * a session has actually ended. The split is the whole point.
 *
 * Nothing here may import `vue`, `vue-router`, `axios` or `pinia` — not even
 * as a type. That is why the redirect target is the plain `AuthRedirect` data
 * shape below rather than vue-router's `RouteLocationRaw`; the caller, which
 * holds the router anyway, is the right place for that dependency.
 */

/** The three states the interceptor distinguishes. `'none'` means stay out. */
export type AuthFailure = 'none' | 'session-expired' | 'password-change-required'

/**
 * A redirect target, shaped so `router.push()` accepts it as-is.
 *
 * Deliberately narrower than vue-router's `RouteLocationRaw`: a union of the
 * two pushes this module produces and nothing else, so a typo in a route name
 * or a stray `query` on the password-change target will not compile. The type
 * costs no import.
 */
export type AuthRedirect =
  | { name: 'login'; query: { redirect: string } }
  | { name: 'change-password' }

/**
 * Requests whose 401 is a legitimate answer rather than a dead session.
 *
 * ⚠️ Matched as a SUBSTRING of the whole url, query string included — so a
 * protected endpoint that merely mentions the prefix (`/api/ponds?redirect=
 * /api/auth/me`) is exempt too. That is a quirk, not a design goal, but it is
 * today's behaviour and `api/interceptor.spec.ts` pins it. Swapping this for a
 * `startsWith` or a pathname comparison is a behaviour change and belongs in
 * its own commit with a red test written on purpose.
 */
const AUTH_ENDPOINT_MARKER = '/api/auth/'

/**
 * Classify a failed response. Cheap, synchronous, and side-effect free — it is
 * called for every rejection the app sees, so it must stay that way.
 *
 * ⚠️ The `AUTH_ENDPOINT_MARKER` exemption applies to the 401 case ONLY. A 403
 * with `password_change_required` redirects even when it came back from an
 * auth endpoint, because the boot probe `GET /api/auth/me` is exactly how a
 * confined user is discovered on page load. Hoisting the url check above both
 * cases — the natural way to "tidy this up" — silently breaks the forced
 * password-change confinement.
 *
 * @param input.status HTTP status, or `undefined` when the request never got a
 *   response (network error, timeout) — neither is an auth failure.
 * @param input.url The request url as axios recorded it, `''` when unknown.
 * @param input.detail The response body's `detail` field, whatever it holds.
 */
export function classifyAuthFailure(input: {
  status: number | undefined
  url: string
  detail: unknown
}): AuthFailure {
  const { status, url, detail } = input

  if (status === 401 && !url.includes(AUTH_ENDPOINT_MARKER)) {
    return 'session-expired'
  }

  if (status === 403 && detail === 'password_change_required') {
    return 'password-change-required'
  }

  return 'none'
}

/**
 * Where to send the user, or `null` when they are already there.
 *
 * The guards exist to stop a redirect loop: a 401 that arrives while the user
 * sits on /login must not push /login again, and the same for a confined user
 * already on /change-password.
 *
 * ⚠️ Returning `null` means "do not push" — it does NOT mean "do nothing". On
 * a session expiry the caller clears the auth store UNCONDITIONALLY, before
 * ever asking this function. Moving that call behind this result leaves a dead
 * session in memory on the login screen.
 *
 * @param currentRouteName The active route's name, `undefined` when it has
 *   none — treated as "not the guarded route", so the redirect goes ahead.
 * @param currentFullPath Carried into `query.redirect` so login can return the
 *   user to the page they lost. Unused for the password-change target, which
 *   is a dead end until the password is actually changed.
 */
export function resolveAuthRedirect(
  failure: Exclude<AuthFailure, 'none'>,
  currentRouteName: string | undefined,
  currentFullPath: string,
): AuthRedirect | null {
  if (failure === 'session-expired') {
    if (currentRouteName === 'login') return null
    return { name: 'login', query: { redirect: currentFullPath } }
  }

  if (failure === 'password-change-required') {
    if (currentRouteName === 'change-password') return null
    return { name: 'change-password' }
  }

  // Unreachable today, and the point is to keep it that way: adding a fourth
  // `AuthFailure` without handling it here is a compile error rather than a
  // silent push to /change-password, which is what the old fall-through did.
  const unhandled: never = failure
  throw new Error(`unhandled auth failure: ${String(unhandled)}`)
}
